import { Injectable, NgZone } from '@angular/core';
import { Platform, AlertController, Alert } from 'ionic-angular';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import 'rxjs/add/observable/empty';
import 'rxjs/add/observable/fromEventPattern';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/map';

@Injectable()
export class SerialService {
  public readonly data$: Observable<Uint8Array>;

  private isOpen = false;
  private port: any;
  private reader: any;
  private writer: any;
  private observerRef: any;
  private activePromptScopes = 0;
  private promptAlert: Alert | null = null;
  private promptPresenting = false;
  private disconnectAlert: Alert | null = null;
  private connectionState = new BehaviorSubject<'connected' | 'disconnected'>('disconnected');
  private dataActive = new BehaviorSubject<boolean>(false);
  private dataActiveTimer: any = null;

  public readonly connectionState$ = this.connectionState.asObservable();
  public readonly dataActive$ = this.dataActive.asObservable();

  // ★ 追加: feature detect を一元化
  private get serial(): any | null {
    return (typeof navigator !== 'undefined' && (navigator as any).serial) ? (navigator as any).serial : null;
  }
  private get hasWebSerial(): boolean {
    return !!this.serial;
  }

  // ★ 追加: ハンドラ参照を保持（remove 用）
  private onConnect = (e: any) => this.handleConnectEvent(e);
  private onDisconnect = (e: any) => this.handleDisconnectEvent(e);

  private static readonly SUPPORTED_DEVS = [
    { usbVendorId: 0x10C4, usbProductId: 0xEA60 }, // CP2102
    { usbVendorId: 0x303A, usbProductId: 0x1001 }, // ESP32-C3/H2
  ];

  constructor(
    private platform: Platform,
    private zone: NgZone,
    private alertCtrl: AlertController
  ) {
    // ★ 変更: 未対応環境では EMPTY にフォールバック
    const connect$ = this.hasWebSerial
      ? Observable.fromEventPattern(
          h => this.serial.addEventListener('connect', h),
          h => this.serial.removeEventListener('connect', h)
        )
      : Observable.empty();

    const disconnect$ = this.hasWebSerial
      ? Observable.fromEventPattern(
          h => this.serial.addEventListener('disconnect', h),
          h => this.serial.removeEventListener('disconnect', h)
        )
      : Observable.empty();

    // ★ 変更: 公式イベントもハンドラ参照を使って登録（fromEventPattern と二重登録したくない場合はどちらか一方に）
    if (this.hasWebSerial) {
      this.serial.addEventListener('connect', this.onConnect);
      this.serial.addEventListener('disconnect', this.onDisconnect);
    }

    connect$
      .switchMap(() =>
        this.hasWebSerial
          ? Observable.fromPromise(this.serial.getPorts())
          : Observable.empty()
      )
      .subscribe((ports: any[]) => {
        if (!this.isPromptScopeActive() || this.isOpen) return;
        if (this.shouldPromptForPorts(ports)) this.promptConnect();
      });

    disconnect$.subscribe(() => this.handleDisconnectEvent(null));

    this.data$ = new Observable<Uint8Array>(observer => {
      this.observerRef = observer;
      return () => {
        // teardown
        if (this.reader) {
          try { this.reader.releaseLock(); } catch {}
          this.reader = null;
        }
        // ★ 追加: イベントリスナの確実な解除（未対応なら何もしない）
        if (this.hasWebSerial) {
          try { this.serial.removeEventListener('connect', this.onConnect); } catch {}
          try { this.serial.removeEventListener('disconnect', this.onDisconnect); } catch {}
        }
      };
    }).share();
  }

  // ★ 追加: イベント受け取りの実体
  private async handleConnectEvent(e: any) {
    if (!this.hasWebSerial || !this.isPromptScopeActive()) return;
    const candidates: any[] = [];

    const pushCandidate = (port: any) => {
      if (!port || typeof port.getInfo !== 'function') return;
      if (candidates.indexOf(port) >= 0) return;
      candidates.push(port);
    };

    if (e && e.port) pushCandidate(e.port);
    if (e && e.target) pushCandidate(e.target);

    try {
      const ports: any[] = await this.serial.getPorts();
      if (Array.isArray(ports)) {
        ports.forEach(pushCandidate);
      }
    } catch (err) {
      console.error('connect event handling failed:', err);
    }

    if (!this.isOpen && this.shouldPromptForPorts(candidates)) {
      this.promptConnect();
    }
  }

  private handleDisconnectEvent(_e: any) {
    this.isOpen = false;
    this.setConnectionState('disconnected');
    this.zone.run(() => {
      if (this.disconnectAlert) return;
      const alert = this.alertCtrl.create({
        title: '計測デバイス切断',
        message: '計測デバイスが切断されました。',
        buttons: ['OK']
      });

      alert.onDidDismiss(() => {
        this.disconnectAlert = null;
      });

      this.disconnectAlert = alert;
      alert.present().catch(err => {
        console.error('disconnect alert present failed:', err);
        this.disconnectAlert = null;
      });
    });
  }

  public async connect(): Promise<void> {
    if (this.isOpen) return;
    if (!this.hasWebSerial) {
      console.warn('Web Serial is not available on this platform.');
      return;
    }
    try {
      this.port = await this.serial.requestPort({
        filters: SerialService.SUPPORTED_DEVS.map(d => ({
          usbVendorId: d.usbVendorId, usbProductId: d.usbProductId
        }))
      });

      await this.port.open({ baudRate: 921600 });
      console.log('Serial Port Connected:', this.port);
      this.isOpen = true;
      this.setConnectionState('connected');
      this.resetDataActive();

      // ★ 追加: writer の取得（send で使う）
      if (this.port.writable) this.writer = this.port.writable.getWriter();

      this.reader = this.port.readable.getReader();
      this.readLoop();
    } catch (err) {
      console.error('Serial connect failed:', err);
    }
  }

  public async disconnect(): Promise<void> {
    console.log('Serial Port will be disconnected:', this.port);
    try {
      if (this.reader) { try { await this.reader.cancel(); } catch {} try { this.reader.releaseLock(); } catch {} this.reader = null; }
      if (this.writer) { try { this.writer.releaseLock(); } catch {} this.writer = null; }
      if (this.port)   { try { await this.port.close(); } catch {} this.port = null; }
    } finally {
      this.isOpen = false;
      this.setConnectionState('disconnected');
    }
  }

  public async tryPromptConnect(): Promise<void> {
    if (!this.hasWebSerial || !this.isPromptScopeActive()) return;
    try {
      const ports: any[] = await this.serial.getPorts();
      if (!this.isOpen && this.shouldPromptForPorts(ports)) this.promptConnect();
    } catch (err) {
      console.error('tryPromptConnect failed:', err);
    }
  }

  public async send(bytes: Uint8Array): Promise<void> {
    if (!this.isOpen || !this.writer) throw new Error('not connected');
    await this.writer.write(bytes);
  }

  private async readLoop() {
    let buffer = new Uint8Array(0);
    while (this.isOpen) {
      try {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (!value) continue;
        const tmp = new Uint8Array(buffer.length + value.length);
        tmp.set(buffer, 0);
        tmp.set(value, buffer.length);
        buffer = tmp;

        const FRAME_SIZE = 24;
        const PAYLOAD_SIZE = 22;
        while (buffer.length >= FRAME_SIZE) {
          if (buffer[0] !== 0xA5 || buffer[1] !== 0x5A) { buffer = buffer.slice(1); continue; }
          const frame = buffer.slice(0, FRAME_SIZE);
          buffer = buffer.slice(FRAME_SIZE);
          const payload = frame.slice(2);
          if (payload.length !== PAYLOAD_SIZE) continue;

          const nowMs: number = Date.now();
          const tsBuf = new Uint8Array(8);
          const dv = new DataView(tsBuf.buffer);
          dv.setUint32(0, nowMs >>> 0, true);
          dv.setUint32(4, Math.floor(nowMs / 0x1_0000_0000) >>> 0, true);

          const out = new Uint8Array(payload.length + tsBuf.length);
          out.set(payload, 0);
          out.set(tsBuf, payload.length);

          // ★ 追加: observerRef の存在チェック
          this.markDataActive();
          if (this.observerRef && this.observerRef.next) this.observerRef.next(out);
        }
      } catch (err) {
        console.error('readLoop failed:', err);
        break;
      }
    }
    if (this.reader) { try { this.reader.releaseLock(); } catch {} this.reader = null; }
    this.isOpen = false;
    this.setConnectionState('disconnected');
  }

  private evaluatePort(port: any): 'supported' | 'unsupported' | 'unknown' {
    if (!port || typeof port.getInfo !== 'function') return 'unknown';
    try {
      const info = port.getInfo();
      if (!info) return 'unknown';
      return SerialService.SUPPORTED_DEVS.some(
        dev => dev.usbVendorId === info.usbVendorId && dev.usbProductId === info.usbProductId
      )
        ? 'supported'
        : 'unsupported';
    } catch { return 'unknown'; }
  }

  private shouldPromptForPorts(ports: any[] | null | undefined): boolean {
    const list = Array.isArray(ports) ? ports : [];
    if (list.length === 0) return false;

    for (const port of list) {
      const state = this.evaluatePort(port);
      if (state === 'supported' || state === 'unknown') return true;
    }

    return false;
  }

  private isSupportedPort(p: any): boolean {
    return this.evaluatePort(p) === 'supported';
  }

  private setConnectionState(state: 'connected' | 'disconnected') {
    if (this.connectionState.getValue() !== state) {
      this.zone.run(() => this.connectionState.next(state));
    }
    if (state === 'disconnected') this.resetDataActive();
  }

  private markDataActive() {
    if (!this.dataActive.getValue()) {
      this.zone.run(() => this.dataActive.next(true));
    }
    if (this.dataActiveTimer) clearTimeout(this.dataActiveTimer);
    this.dataActiveTimer = setTimeout(() => {
      this.zone.run(() => {
        this.dataActive.next(false);
        this.dataActiveTimer = null;
      });
    }, 2000);
  }

  private resetDataActive() {
    if (this.dataActiveTimer) {
      clearTimeout(this.dataActiveTimer);
      this.dataActiveTimer = null;
    }
    if (this.dataActive.getValue()) {
      this.zone.run(() => this.dataActive.next(false));
    }
  }

  private promptConnect() {
    if (!this.isPromptScopeActive() || this.isOpen || this.promptAlert || this.promptPresenting) return;
    this.zone.run(() => {
      try {
        this.promptPresenting = true;
        const alert = this.alertCtrl.create({
          title: '計測デバイス検出',
          message: '計測デバイスが接続されています。ログ収集を開始しますか？',
          buttons: [
            { text: 'キャンセル' },
            { text: '開始', handler: () => { this.connect(); } }
          ]
        });

        alert.onDidDismiss(() => {
          this.promptAlert = null;
          this.promptPresenting = false;
        });

        this.promptAlert = alert;
        alert.present()
          .catch(err => {
            console.error('present prompt failed:', err);
            this.promptAlert = null;
            this.promptPresenting = false;
          });
      } catch (err) {
        console.error('promptConnect failed:', err);
        this.promptPresenting = false;
      }
    });
  }

  public enterPromptScope(): () => void {
    this.activePromptScopes += 1;
    let released = false;

    return () => {
      if (released) return;
      released = true;
      this.activePromptScopes = Math.max(0, this.activePromptScopes - 1);

      if (!this.isPromptScopeActive()) {
        if (this.promptAlert) {
          try {
            this.promptAlert.dismiss();
          } catch (err) {
            console.warn('failed to dismiss prompt alert:', err);
          }
        }
        this.promptPresenting = false;
      }
    };
  }

  private isPromptScopeActive(): boolean {
    return this.activePromptScopes > 0;
  }
}



// WEBPACK FOOTER //
// ./src/services/serial-log.ts